import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import {
  OrganizationItemCreate,
  OrganizationItemDelete,
  OrganizationItemInitialState,
  OrganizationItemLoad,
  OrganizationItemUpdate,
} from './organization-item.actions';
import { OrganizationModel } from '../models/organization.model';
import { catchError, map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { OrganizationItemService } from './organization-item.service';
import { NavigateUserService } from '../../../../services/navigate-user.service';
import { Error, CustomHttpErrorResponse } from '../../../../../common';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface OrganizationItemStateModel extends OrganizationModel {
  loaded: boolean;
}

const initialState: OrganizationItemStateModel = {
  ...new OrganizationModel(),
  loaded: false,
};

@State<OrganizationItemStateModel>({
  name: 'organizationItem',
  defaults: initialState,
})
@Injectable()
export class OrganizationItemState {
  constructor(
    private organizationItemService: OrganizationItemService,
    private navigateUserService: NavigateUserService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getState(state: OrganizationItemStateModel): OrganizationItemStateModel {
    return state;
  }

  @Selector()
  static getOrganizationId(state: OrganizationItemStateModel): number {
    return state.id;
  }

  @Selector()
  static getOrganizationName(state: OrganizationItemStateModel): string {
    return state.organizationName;
  }

  @Selector()
  static getLoaded(state: OrganizationItemStateModel): boolean {
    return state.loaded;
  }

  @Action(OrganizationItemLoad)
  getOrganizationDetails(
    ctx: StateContext<OrganizationItemStateModel>,
    { payload }: OrganizationItemLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.organizationItemService.getOrganizationDetails(payload).pipe(
      take(1),
      map((organizationDetails: OrganizationModel) => {
        ctx.patchState({ ...organizationDetails, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(OrganizationItemCreate)
  createNewOrganization(
    ctx: StateContext<OrganizationItemStateModel>,
    { payload }: OrganizationItemCreate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.organizationItemService.createOrganization(payload).pipe(
      take(1),
      map(() => {
        this.notificationService.success('api.organization.success.create');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/administration/organizations');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(OrganizationItemUpdate)
  updateOrganization(
    ctx: StateContext<OrganizationItemStateModel>,
    { payload }: OrganizationItemUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.organizationItemService.updateOrganization(payload).pipe(
      take(1),
      map((organizationDetails: OrganizationModel) => {
        this.notificationService.success('api.organization.success.update');
        ctx.patchState({ ...organizationDetails, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(OrganizationItemDelete)
  deleteOrganization(
    ctx: StateContext<OrganizationItemStateModel>,
    { payload }: OrganizationItemDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | Observable<void>> {
    return this.organizationItemService.deleteOrganization(payload.organizationId).pipe(
      take(1),
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.organization.success.delete');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/administration/organizations');
        return ctx.dispatch(new OrganizationItemInitialState());
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(OrganizationItemInitialState)
  setInitialState(ctx: StateContext<OrganizationItemStateModel>): void {
    ctx.setState(initialState);
  }
}
